import {register} from '../modules/eventMap.js';
import {initMarked, renderMarkdown} from '../vendor/marked/service.js';
import {escapeHtml} from '../vendor/marked/utils.js';

// Markdown token patterns for incremental parsing
const MARKDOWN_PATTERNS = {
    heading: /^(#{1,6})\s/,
    bold: /\*\*([^*]+)\*\*/g,
    blockquote: /^>\s?(.*)/gm,
    italic: /\*([^*]+)\*/g,
    code: /`([^`]+)`/g,
    codeBlock: /```[\s\S]*?```/g,
    list: /^(\s*)([-*+]|\d+\.)\s/,
    link: /\[([^\]]+)\]\(([^)]+)\)/g,
    lineBreak: /\n/g
};

// Check if a markdown element is complete
const isElementComplete = (text, pattern) => {
    if (pattern === MARKDOWN_PATTERNS.heading) {
        return /\n/.test(text) || text.length > 100;
    }
    if (pattern === MARKDOWN_PATTERNS.bold) {
        return (text.match(/\*\*/g) || []).length % 2 === 0;
    }
    if (pattern === MARKDOWN_PATTERNS.blockquote) {
        // Consider blockquote complete if the text includes an empty quote line
        // or a double line break
        return /\n\s*>?\s*\n/.test(text) || /\n{2,}/.test(text);
    }
    if (pattern === MARKDOWN_PATTERNS.italic) {
        return (text.match(/\*/g) || []).length % 2 === 0;
    }
    if (pattern === MARKDOWN_PATTERNS.code) {
        return (text.match(/`/g) || []).length % 2 === 0;
    }
    if (pattern === MARKDOWN_PATTERNS.codeBlock) {
        return (
            text.includes('```') && (text.match(/```/g) || []).length % 2 === 0
        );
    }
    if (pattern === MARKDOWN_PATTERNS.list) {
        return /\n/.test(text) && !text.match(/^(\s*)([-*+]|\d+\.)\s/);
    }
    if (pattern === MARKDOWN_PATTERNS.link) {
        return text.includes('](') && text.includes(')');
    }
    return true; // Default to complete
};

// Create incremental markdown renderer
const createIncrementalRenderer = (el, options = {}) => {
    const {headingOffset, maxBufferSize = 10000} = options;

    let buffer = '';
    let renderTimeout = null;
    let markedInstance = null;
    let isFinalized = false;

    const init = async () => {
        markedInstance = await initMarked(headingOffset);
    };

    // Process buffer and render complete elements
    const processBuffer = async () => {
        if (isFinalized || buffer.length === 0) {
            return;
        }

        let processedText = '';
        let remainingText = buffer;
        let hasChanges = false;

        // Process each markdown pattern
        for (const [, pattern] of Object.entries(MARKDOWN_PATTERNS)) {
            if (isElementComplete(remainingText, pattern)) {
                const escapedResponse = escapeHtml(remainingText);
                const processed = await renderMarkdown(
                    escapedResponse,
                    markedInstance
                );

                if (processed !== remainingText) {
                    processedText = processed;
                    remainingText = '';
                    hasChanges = true;
                }
            }
        }

        // If no patterns matched or elements are incomplete, keep as plain text
        if (!hasChanges) {
            processedText = remainingText;
        }

        // Update element content
        if (processedText !== el.innerHTML) {
            el.innerHTML = processedText;
        }
    };

    // Schedule rendering with requestAnimationFrame
    const scheduleRender = () => {
        if (renderTimeout) {
            cancelAnimationFrame(renderTimeout);
        }

        renderTimeout = requestAnimationFrame(async () => {
            await processBuffer();
            renderTimeout = null;
        });
    };

    // Add text delta to buffer
    const appendDelta = (delta) => {
        if (isFinalized) {
            return;
        }

        buffer += delta;

        // Prevent buffer from growing too large
        if (buffer.length > maxBufferSize) {
            buffer = buffer.slice(-maxBufferSize / 2);
        }

        scheduleRender();
    };

    // Finalize rendering - process any remaining incomplete elements
    const finalize = async () => {
        isFinalized = true;

        if (renderTimeout) {
            cancelAnimationFrame(renderTimeout);
            renderTimeout = null;
        }

        // Force final render with complete buffer
        await processBuffer();

        // Clean up
        buffer = '';
    };

    // Initialise Marked
    init();

    return {
        appendDelta,
        finalize,
        getBuffer: () => buffer,
        isFinalized: () => isFinalized
    };
};

// Create streaming markdown renderer for chat responses
const createStreamingRenderer = (el, elContainer, options = {}) => {
    const renderer = createIncrementalRenderer(el, options);

    return {
        ...renderer,
        // Override appendDelta to handle streaming-specific logic
        appendDelta: (delta) => {
            renderer.appendDelta(delta);

            // Auto-scroll to keep content visible
            elContainer.scrollIntoView({behavior: 'smooth', block: 'end'});
        }
    };
};

// Register the component
const incrementalMarkdown = () => {
    register('incrementalMarkdown', {
        createRenderer: createIncrementalRenderer,
        createStreamingRenderer
    });
};

export {incrementalMarkdown, createStreamingRenderer};
